/**
 * VaultImportController — page /coffre-import.html (EPIC-035 / 35.6, spec §24.D).
 *
 * Dropzone + formulaire de métadonnées pour l'upload manuel d'un document
 * dans le coffre-fort. Validation client (MIME, taille, champs obligatoires),
 * soumission `multipart/form-data` vers POST /v1/vault, redirection au
 * succès vers /coffre.html?highlight={id}.
 *
 * Endpoints consommés :
 *   - GET  /v1/vault             → meta.quota (affichage barre quota)
 *   - POST /v1/vault (multipart) → 201 Document
 *
 * Décision tranchée W1 (EPIC-035) : remplace la modale décrite en spec §24.D
 * par une page dédiée (cf. memory `feedback_no_modal_dedicated_edit_page.md`).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const FISCAL_YEAR_HISTORY = 5;
const SIZE_UNITS = ["o", "Ko", "Mo", "Go"];

const FIELD_TO_TARGET = {
  file: "fileError",
  title: "titleError",
  category: "categoryError",
  fiscalYear: "fiscalYearError",
  documentDate: "documentDateError",
};

const FIELD_TO_INPUT_TARGET = {
  file: "fileInput",
  title: "titleInput",
  category: "categoryInput",
  fiscalYear: "fiscalYearInput",
  documentDate: "documentDateInput",
};

const ERROR_CODE_MESSAGES = {
  required: "Ce champ est obligatoire.",
  invalid_format: "Format invalide.",
  out_of_range: "Valeur hors bornes.",
  upload_error: "Le téléversement du fichier a échoué.",
  vault_file_too_large: "Fichier trop volumineux (max 10 Mo).",
  vault_invalid_mime: "Format de fichier non accepté. Formats acceptés : PDF, JPEG, PNG.",
  vault_quota_exceeded: "Quota d'archivage atteint. Archivez des documents existants pour libérer de l'espace.",
};

export default class VaultImportController extends BaseController {
  static targets = [
    "dropzone",
    "fileInput",
    "selectedFile",
    "fileError",
    "titleInput",
    "titleError",
    "categoryInput",
    "categoryError",
    "fiscalYearInput",
    "fiscalYearError",
    "documentDateInput",
    "documentDateError",
    "notesInput",
    "submitButton",
    "quotaText",
    "quotaBar",
    "quotaFill",
  ];

  connect() {
    this._populateFiscalYears();
    this._loadQuota();
  }

  _populateFiscalYears() {
    if (!this.hasFiscalYearInputTarget) return;
    const select = this.fiscalYearInputTarget;
    const placeholder = select.querySelector("option[value='']");
    select.textContent = "";
    if (placeholder) select.appendChild(placeholder);

    const currentYear = new Date().getFullYear();
    for (let i = 0; i <= FISCAL_YEAR_HISTORY; i += 1) {
      const year = currentYear - i;
      const option = document.createElement("option");
      option.value = String(year);
      option.textContent = String(year);
      select.appendChild(option);
    }
  }

  async _loadQuota() {
    try {
      const resp = await api.vault.list({ perPage: 1 });
      this._renderQuota(resp?.meta?.quota || null);
    } catch {
      // Silencieux : la barre de quota reste sur sa valeur par défaut
      // (l'API renverra l'erreur de quota à la soumission le cas échéant).
    }
  }

  _renderQuota(quota) {
    if (!quota || typeof quota.usedBytes !== "number" || typeof quota.quotaBytes !== "number") {
      return;
    }
    const usedBytes = Math.max(0, Math.round(quota.usedBytes));
    const quotaBytes = Math.max(1, Math.round(quota.quotaBytes));
    const usedPercent = Math.min(
      100,
      Math.max(0, Math.round(quota.usedPercent ?? (usedBytes / quotaBytes) * 100)),
    );

    if (this.hasQuotaTextTarget) {
      this.quotaTextTarget.textContent = `${this._formatSize(usedBytes)} / ${this._formatSize(quotaBytes)}`;
    }
    if (this.hasQuotaFillTarget) {
      this.quotaFillTarget.style.width = `${usedPercent}%`;
    }
    if (this.hasQuotaBarTarget) {
      this.quotaBarTarget.setAttribute("aria-valuenow", String(usedPercent));
    }
  }

  // ---------------------------------------------------------------------------
  // Drag & drop
  // ---------------------------------------------------------------------------

  handleDragOver(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) this.dropzoneTarget.dataset.dragover = "true";
  }

  handleDragLeave(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) delete this.dropzoneTarget.dataset.dragover;
  }

  handleFileDrop(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) delete this.dropzoneTarget.dataset.dragover;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (this.hasFileInputTarget) {
      const dt = new DataTransfer();
      dt.items.add(file);
      this.fileInputTarget.files = dt.files;
    }
    this._onFileSelected(file);
  }

  handleFileChange(event) {
    const file = event.target.files?.[0] || null;
    this._onFileSelected(file);
  }

  _onFileSelected(file) {
    this._clearFieldError("file");
    if (!file) {
      this._hideSelectedFile();
      return;
    }
    const clientError = this._validateFileClient(file);
    if (clientError) {
      this._showFieldError("file", clientError);
      this._hideSelectedFile();
      return;
    }
    this._showSelectedFile(file);
  }

  _validateFileClient(file) {
    if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
      return ERROR_CODE_MESSAGES.vault_invalid_mime;
    }
    if (file.size > MAX_FILE_BYTES) {
      return ERROR_CODE_MESSAGES.vault_file_too_large;
    }
    return null;
  }

  _showSelectedFile(file) {
    if (!this.hasSelectedFileTarget) return;
    this.selectedFileTarget.textContent =
      `Fichier sélectionné : ${file.name} (${this._formatSize(file.size)})`;
    this.selectedFileTarget.hidden = false;
  }

  _hideSelectedFile() {
    if (!this.hasSelectedFileTarget) return;
    this.selectedFileTarget.textContent = "";
    this.selectedFileTarget.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // Date du document → pré-remplissage année fiscale
  // ---------------------------------------------------------------------------

  handleDateChange(event) {
    this._clearFieldError("documentDate");
    const value = event.target?.value || "";
    const year = this._extractYear(value);
    if (year === null) return;
    if (!this.hasFiscalYearInputTarget) return;

    const select = this.fiscalYearInputTarget;
    const hasOption = Array.from(select.options).some((opt) => opt.value === String(year));
    if (!hasOption) {
      const option = document.createElement("option");
      option.value = String(year);
      option.textContent = String(year);
      const placeholder = select.querySelector("option[value='']");
      if (placeholder && placeholder.nextSibling) {
        select.insertBefore(option, placeholder.nextSibling);
      } else {
        select.appendChild(option);
      }
    }
    select.value = String(year);
    this._clearFieldError("fiscalYear");
  }

  _extractYear(isoDate) {
    if (typeof isoDate !== "string" || isoDate.length < 4) return null;
    const year = Number(isoDate.slice(0, 4));
    if (!Number.isFinite(year) || year < 1900 || year > 2100) return null;
    return year;
  }

  // ---------------------------------------------------------------------------
  // Submission
  // ---------------------------------------------------------------------------

  async handleSubmit(event) {
    event.preventDefault();
    this._clearAllFieldErrors();

    const payload = this._collectPayload();
    const clientErrors = this._validateClient(payload);
    if (clientErrors.length > 0) {
      clientErrors.forEach(({ field, message }) => this._showFieldError(field, message));
      this._focusFirstError(clientErrors[0].field);
      return;
    }

    this._setSubmitting(true);
    try {
      const resp = await api.vault.create({
        file: payload.file,
        title: payload.title,
        category: payload.category,
        documentDate: payload.documentDate,
        fiscalYear: String(payload.fiscalYear),
        ...(payload.notes !== null ? { notes: payload.notes } : {}),
      });
      const id = resp?.data?.id;
      if (!id) {
        this.showError("Réponse inattendue du serveur.");
        this._setSubmitting(false);
        return;
      }
      window.location.assign(`/coffre.html?highlight=${encodeURIComponent(id)}`);
    } catch (err) {
      this._handleSubmitError(err);
      this._setSubmitting(false);
    }
  }

  _collectPayload() {
    const file = this.hasFileInputTarget ? this.fileInputTarget.files?.[0] || null : null;
    const title = this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : "";
    const category = this.hasCategoryInputTarget ? this.categoryInputTarget.value : "";
    const documentDate = this.hasDocumentDateInputTarget ? this.documentDateInputTarget.value : "";
    const fiscalYearRaw = this.hasFiscalYearInputTarget ? this.fiscalYearInputTarget.value : "";
    const fiscalYear = fiscalYearRaw === "" ? null : Number(fiscalYearRaw);
    const notesRaw = this.hasNotesInputTarget ? this.notesInputTarget.value.trim() : "";
    const notes = notesRaw === "" ? null : notesRaw;

    return { file, title, category, documentDate, fiscalYear, notes };
  }

  _validateClient(payload) {
    const errors = [];

    if (!payload.file) {
      errors.push({ field: "file", message: ERROR_CODE_MESSAGES.required });
    } else {
      const fileError = this._validateFileClient(payload.file);
      if (fileError) errors.push({ field: "file", message: fileError });
    }

    if (!payload.title) {
      errors.push({ field: "title", message: ERROR_CODE_MESSAGES.required });
    } else if (payload.title.length > 120) {
      errors.push({ field: "title", message: "120 caractères maximum." });
    }

    if (!payload.category) {
      errors.push({ field: "category", message: ERROR_CODE_MESSAGES.required });
    }

    if (!payload.documentDate) {
      errors.push({ field: "documentDate", message: ERROR_CODE_MESSAGES.required });
    } else if (this._extractYear(payload.documentDate) === null) {
      errors.push({ field: "documentDate", message: ERROR_CODE_MESSAGES.invalid_format });
    }

    if (payload.fiscalYear === null || Number.isNaN(payload.fiscalYear)) {
      errors.push({ field: "fiscalYear", message: ERROR_CODE_MESSAGES.required });
    } else if (payload.fiscalYear < 1900 || payload.fiscalYear > 2100) {
      errors.push({ field: "fiscalYear", message: ERROR_CODE_MESSAGES.out_of_range });
    }

    return errors;
  }

  _handleSubmitError(err) {
    if (err instanceof APIError) {
      const data = err.data || {};
      const fields = Array.isArray(data?.details?.fields) ? data.details.fields : [];
      if (fields.length > 0) {
        fields.forEach(({ field, code }) => {
          if (!field || !FIELD_TO_TARGET[field]) return;
          const message = ERROR_CODE_MESSAGES[code] || data.message || "Champ invalide.";
          this._showFieldError(field, message);
        });
        const firstField = fields.find((f) => FIELD_TO_TARGET[f.field])?.field;
        if (firstField) this._focusFirstError(firstField);
        return;
      }
      const message = (typeof data.message === "string" && data.message)
        || ERROR_CODE_MESSAGES[data.error]
        || "L'import a échoué.";
      this.showError(message);
      return;
    }
    this.showError("L'import a échoué. Réessayez.");
  }

  _setSubmitting(isSubmitting) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = isSubmitting;
    this.submitButtonTarget.textContent = isSubmitting
      ? "Import en cours…"
      : "Importer le document";
  }

  // ---------------------------------------------------------------------------
  // Error display helpers
  // ---------------------------------------------------------------------------

  _showFieldError(field, message) {
    const targetName = FIELD_TO_TARGET[field];
    if (!targetName) return;
    const hasFn = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    const refFn = `${targetName}Target`;
    if (!this[hasFn]) return;
    const el = this[refFn];
    el.textContent = message;
    el.hidden = false;

    const inputName = FIELD_TO_INPUT_TARGET[field];
    const inputHasFn = `has${inputName.charAt(0).toUpperCase()}${inputName.slice(1)}Target`;
    const inputRefFn = `${inputName}Target`;
    if (this[inputHasFn]) {
      this[inputRefFn].setAttribute("aria-invalid", "true");
    }
  }

  _clearFieldError(field) {
    const targetName = FIELD_TO_TARGET[field];
    if (!targetName) return;
    const hasFn = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    const refFn = `${targetName}Target`;
    if (!this[hasFn]) return;
    const el = this[refFn];
    el.textContent = "";
    el.hidden = true;

    const inputName = FIELD_TO_INPUT_TARGET[field];
    const inputHasFn = `has${inputName.charAt(0).toUpperCase()}${inputName.slice(1)}Target`;
    const inputRefFn = `${inputName}Target`;
    if (this[inputHasFn]) {
      this[inputRefFn].removeAttribute("aria-invalid");
    }
  }

  _clearAllFieldErrors() {
    Object.keys(FIELD_TO_TARGET).forEach((field) => this._clearFieldError(field));
  }

  _focusFirstError(field) {
    const inputName = FIELD_TO_INPUT_TARGET[field];
    if (!inputName) return;
    const hasFn = `has${inputName.charAt(0).toUpperCase()}${inputName.slice(1)}Target`;
    const refFn = `${inputName}Target`;
    if (this[hasFn]) this[refFn].focus();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatSize(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n < 0) return "—";
    if (n === 0) return "0 o";
    let value = n;
    let unit = 0;
    while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
      value /= 1024;
      unit += 1;
    }
    const formatted = value >= 100 || unit === 0
      ? Math.round(value).toString()
      : value.toFixed(value >= 10 ? 0 : 1).replace(".", ",");
    return `${formatted} ${SIZE_UNITS[unit]}`;
  }
}
